import React, { useMemo } from 'react';
import { useEditor, Node } from '@splashthat/splash-cms-engine';
import PropTypes from 'prop-types';

import TouchpointContainer from 'common/components/TouchpointEditor/components/TouchpointContainer';
import BreakpointPreviewer from 'common/components/TouchpointEditor/components/BreakpointPreviewer';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { usePreview } from 'common/components/TouchpointEditor/hooks/usePreview';

import { transformStructureDataForEditor } from 'common/compounds/VirtualEventRenderer/states/lib/transformStructureDataForEditor';
import TextParagraph from 'common/compounds/VirtualEventRenderer/states/PreCheckIn/components/nodes/Paragraph/SubTypes/TextParagraph';

import {
    NODE_ID_PAGE_CONTAINER,
    NODE_DISPLAY_NAME_PAGE_CONTAINER,
    NODE_ID_CONTENT_CONTAINER,
    NODE_DISPLAY_NAME_CONTENT_CONTAINER,
    NODE_ID_LOGO,
    NODE_DISPLAY_NAME_LOGO,
    NODE_ID_EVENT_TITLE,
    NODE_DISPLAY_NAME_EVENT_TITLE,
    NODE_ID_TEXT,
    NODE_DISPLAY_NAME_TEXT,
    NODE_ID_FOOTER_CONTAINER,
    NODE_DISPLAY_NAME_FOOTER_CONTAINER,
    NODE_ID_CONTACT_ORGANIZER_BUTTON,
    NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON,
    NODE_ID_VISIT_EVENT_PAGE_BUTTON,
    NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON,
    NODE_ID_CUSTOM_BUTTON,
    NODE_ID_CUSTOM_BUTTON_TWO,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO,
    NODE_ID_HEADER,
    NODE_DISPLAY_NAME_HEADER,
} from './constants';
import EditorTools from '../components/sidebar';
import {
    VirtualGuestExperienceContainer,
    FooterBlockContainer,
    LeftLinkContainer,
    RightLinkContainer,
} from '../../components/Common.styles';
import PageContainer from '../../components/nodes/Block/SubTypes/PageContainer';
import Header from '../../components/nodes/Block/SubTypes/Header';
import ContentContainer from '../../components/nodes/Block/SubTypes/ContentContainer';
import FooterContainer from '../../components/nodes/Block/SubTypes/FooterContainer';
import EventTitleHeadline from '../../components/nodes/Headline/SubTypes/EventTitleHeadline';
import ViewEventPageLink from '../../components/nodes/Link/SubTypes/ViewEventPageLink';
import ContactTheOrganizer from '../../components/nodes/Link/SubTypes/ContactTheOrganizer';
import ContentLogo from '../../components/nodes/Logo/SubTypes/ContentLogo';
import CustomButtonLight from '../../components/nodes/Button/SubTypes/CustomButtonLight';

export default function PreCheckIn({ structure, currentPageState }) {
    const { event } = useTouchpointData();
    const { shouldPreview } = usePreview({ event });
    const editor = useEditor((state) => ({
        editing: state.editing,
    }));

    const preCheckInPageStructure = useMemo(
        () => transformStructureDataForEditor(structure, currentPageState),
        [structure, currentPageState],
    );

    return (
        <TouchpointContainer editing={editor.editing}>
            <BreakpointPreviewer enabled={shouldPreview}>
                <VirtualGuestExperienceContainer $editing={editor.editing}>
                    <Node
                        is={PageContainer}
                        id={NODE_ID_PAGE_CONTAINER}
                        displayName={NODE_DISPLAY_NAME_PAGE_CONTAINER}
                        {...preCheckInPageStructure[NODE_ID_PAGE_CONTAINER]}
                    >
                        <Node
                            is={Header}
                            id={NODE_ID_HEADER}
                            displayName={NODE_DISPLAY_NAME_HEADER}
                            {...preCheckInPageStructure[NODE_ID_HEADER]}
                        />
                        <Node
                            is={ContentContainer}
                            id={NODE_ID_CONTENT_CONTAINER}
                            displayName={NODE_DISPLAY_NAME_CONTENT_CONTAINER}
                            {...preCheckInPageStructure[NODE_ID_CONTENT_CONTAINER]}
                        >
                            <Node
                                is={ContentLogo}
                                id={NODE_ID_LOGO}
                                displayName={NODE_DISPLAY_NAME_LOGO}
                                {...preCheckInPageStructure[NODE_ID_LOGO]}
                            />
                            <Node
                                is={EventTitleHeadline}
                                id={NODE_ID_EVENT_TITLE}
                                displayName={NODE_DISPLAY_NAME_EVENT_TITLE}
                                {...preCheckInPageStructure[NODE_ID_EVENT_TITLE]}
                            />
                            <Node
                                is={TextParagraph}
                                id={NODE_ID_TEXT}
                                displayName={NODE_DISPLAY_NAME_TEXT}
                                {...preCheckInPageStructure[NODE_ID_TEXT]}
                            />
                        </Node>
                        <Node
                            is={FooterContainer}
                            id={NODE_ID_FOOTER_CONTAINER}
                            displayName={NODE_DISPLAY_NAME_FOOTER_CONTAINER}
                            {...preCheckInPageStructure[NODE_ID_FOOTER_CONTAINER]}
                        >
                            <FooterBlockContainer>
                                <LeftLinkContainer>
                                    <Node
                                        is={ContactTheOrganizer}
                                        id={NODE_ID_CONTACT_ORGANIZER_BUTTON}
                                        displayName={NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON}
                                        {...preCheckInPageStructure[NODE_ID_CONTACT_ORGANIZER_BUTTON]}
                                    />
                                    <Node
                                        is={ViewEventPageLink}
                                        id={NODE_ID_VISIT_EVENT_PAGE_BUTTON}
                                        displayName={NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON}
                                        {...preCheckInPageStructure[NODE_ID_VISIT_EVENT_PAGE_BUTTON]}
                                    />
                                </LeftLinkContainer>
                                <RightLinkContainer>
                                    <Node
                                        is={CustomButtonLight}
                                        id={NODE_ID_CUSTOM_BUTTON}
                                        displayName={NODE_DISPLAY_NAME_CUSTOM_BUTTON}
                                        {...preCheckInPageStructure[NODE_ID_CUSTOM_BUTTON]}
                                    />
                                    <Node
                                        is={CustomButtonLight}
                                        id={NODE_ID_CUSTOM_BUTTON_TWO}
                                        displayName={NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO}
                                        {...preCheckInPageStructure[NODE_ID_CUSTOM_BUTTON_TWO]}
                                    />
                                </RightLinkContainer>
                            </FooterBlockContainer>
                        </Node>
                    </Node>
                </VirtualGuestExperienceContainer>
            </BreakpointPreviewer>
            {editor.editing && <EditorTools />}
        </TouchpointContainer>
    );
}

PreCheckIn.propTypes = {
    structure: PropTypes.object,
    currentPageState: PropTypes.string.isRequired,
};

PreCheckIn.defaultProps = {
    structure: null,
};
